import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchLongShortRatio } from '../lib/coinglass';

interface LongShortRatioProps {
  symbol?: string;
}

const LongShortRatio: React.FC<LongShortRatioProps> = ({ symbol = 'BTC' }) => {  
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['longShortRatio', symbol],
    queryFn: async () => {
      const result = await fetchLongShortRatio(symbol);
      if (!result) return null;
      return {
        longRatio: result.longRatio,
        shortRatio: result.shortRatio,
        timestamp: result.timestamp,
        symbol: result.symbol
      };
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (error) return null;
  if (isLoading || !data) return null;
  if (!data || data.longRatio === 0 && data.shortRatio === 0) return null;
  const longPercentage = data?.longRatio || 0;
  const shortPercentage = data?.shortRatio || 0;

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Long/Short Ratio</h3>
          <p className="text-sm text-gray-400">{data?.symbol || symbol}</p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-sm text-gray-400">Longs</div>
          <div className="flex items-center gap-2">
            <ArrowUpIcon className="text-green-500" />
            <span className="text-2xl font-bold text-white">
              {formatPercentage(longPercentage)}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm text-gray-400">Shorts</div>
          <div className="flex items-center gap-2">            <ArrowDownIcon className="text-red-500" />
            <span className="text-2xl font-bold text-white">
              {formatPercentage(shortPercentage)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-800">
        <div className="w-full h-4 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-red-500"
            style={{ width: `${longPercentage * 100}%` }}
          />
        </div>
        
        <div className="mt-2 text-xs text-gray-400">
          Last updated: {new Date(data?.timestamp || Date.now()).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default LongShortRatio;